import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useLocalStorage from 'react-localstorage-hook';
import { isEmpty } from 'lodash-es';
import { FlexBox } from '../common/FlexBox';
import { MonacoEditor } from '../monaco-editor';
import { minnieMouse } from '../../mocks/standard-events/patients';

// Sample Claim payloads matching the schema
const predefinedClaimStartedPayloads: Record<string, any> = {
  'Sample Claim Started A': {
    patient: minnieMouse,
    identifiers: { ehrClaimId: 'claimA', encounterId: 'encA' },
    basicInformation: {
      serviceDate: '2024-05-01',
      claimDate: '2024-05-02',
      claimStatus: 'Pending',
      insuranceName: 'Acme Insurance',
      placeOfService: { code: '11', description: 'Office' },
    },
    renderingProvider: {
      ehrProviderId: 'provA',
      demographics: { firstName: 'John', lastName: 'Doe' },
    },
    serviceLines: [],
    additionalDiagnoses: [],
  },
};

export const ClaimStartedTab = () => {
  const [payload, setPayload] = useState<any>();
  const [predefinedPayload, setPredefinedPayload] = useState<string>('');
  const [lastUsedPayload, setLastUsedPayload] = useLocalStorage('lastClaimStartedPayload', '');
  const [, setShouldPublish] = useLocalStorage('shouldPublishClaimStarted', false);

  useEffect(() => {
    handlePredefinedPayloadChange({ target: { value: lastUsedPayload } } as SelectChangeEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setShouldPublish(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const handleButtonClick = useCallback(
    (e) => {
      e.stopPropagation();
      setShouldPublish(true);
    },
    [setShouldPublish],
  );

  const handlePredefinedPayloadChange = useCallback(
    ({ target: { value } }: SelectChangeEvent) => {
      setPredefinedPayload(value);
      setLastUsedPayload(value);
      setPayload(predefinedClaimStartedPayloads[value]);
    },
    [setLastUsedPayload],
  );

  return (
    <Accordion id="claimStartedTab">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>claimStarted</Typography>
        <Select
          displayEmpty
          value={predefinedPayload}
          id="claim-started-select"
          size="small"
          onChange={handlePredefinedPayloadChange}
          onClick={handleSelectClick}
        >
          <MenuItem value="">Choose</MenuItem>
          {Object.keys(predefinedClaimStartedPayloads).map((value) => (
            <MenuItem value={value} key={value} id={`claim-started-${value.replaceAll(' ', '-')}`}>
              {value}
            </MenuItem>
          ))}
        </Select>
        <Button
          id="publish-claim-started"
          disabled={isEmpty(predefinedPayload)}
          fullWidth
          onClick={handleButtonClick}
          variant="contained"
        >
          Publish
        </Button>
      </AccordionSummary>
      <AccordionDetails>
        <MonacoEditor onChange={setPayload} height="20rem" value={payload} />
        <FlexBox>
          <TextField
            id="claimStartedPayload"
            variant="outlined"
            size="small"
            value={JSON.stringify(payload)}
            sx={{ display: 'none' }}
          />
        </FlexBox>
      </AccordionDetails>
    </Accordion>
  );
};
